// src/pages/document/DocumentNumberingSchemeCreatePage.tsx

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  ErrorAlert,
} from '@react/components';
import { FormRow, SegmentedControl } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';

const PATTERNS = [
  { value: 'SEQUENTIAL', labelKey: 'document.numberingField.patternSequential' },
  { value: 'YEAR_SEQUENTIAL', labelKey: 'document.numberingField.patternYearSequential' },
  { value: 'CATEGORY_SEQUENTIAL', labelKey: 'document.numberingField.patternCategorySequential' },
  { value: 'YEAR_CATEGORY', labelKey: 'document.numberingField.patternYearCategory' },
];

/**
 * Document numbering scheme create page.
 */
export const DocumentNumberingSchemeCreatePage = observer(() => {
  const navigate = useNavigate();
  const store = useDocument();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    prefix: '',
    separator: '-',
    pattern: 'SEQUENTIAL',
    counter_digits: 3,
    document_type_id: '',
    is_active: true,
  });

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await store.CreateNumberingScheme(formData as any);
      navigate('/documents/numbering-schemes');
    } catch {
      // Error is handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <h1 className="sr-only">{t('document.createNumberingScheme')}</h1>
        <nav className="text-sm">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/documents/numbering-schemes" className="text-muted-foreground hover:text-foreground">
            {t('document.numberingSchemes')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('common.create')}</span>
        </nav>
      </div>

      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <form onSubmit={handleSubmit} noValidate>
        <Card>
          <CardHeader>
            <CardTitle>{t('document.numberingSchemeDetails')}</CardTitle>
            <CardDescription>{t('document.fillInfo')}</CardDescription>
          </CardHeader>
          <CardContent className="divide-y divide-border/60">
            <FormRow label={t('document.numberingField.name')} htmlFor="scheme-name">
              <input
                id="scheme-name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={t('document.numberingField.namePlaceholder')}
                autoCapitalize="off"
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('document.numberingField.description')} htmlFor="scheme-desc">
              <input
                id="scheme-desc"
                type="text"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={t('document.numberingField.descriptionPlaceholder')}
                autoCapitalize="off"
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('document.numberingField.prefix')} htmlFor="scheme-prefix">
              <input
                id="scheme-prefix"
                type="text"
                value={formData.prefix}
                onChange={(e) => handleChange('prefix', e.target.value)}
                placeholder={t('document.numberingField.prefixPlaceholder')}
                autoCapitalize="off"
                className="w-40 rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('document.numberingField.separator')} htmlFor="scheme-separator">
              <input
                id="scheme-separator"
                type="text"
                value={formData.separator}
                onChange={(e) => handleChange('separator', e.target.value)}
                placeholder={t('document.numberingField.separatorPlaceholder')}
                autoCapitalize="off"
                className="w-20 rounded-md border border-input bg-background px-3 py-1.5 text-center font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('document.numberingField.pattern')}>
              <SegmentedControl
                aria-label={t('document.numberingField.pattern')}
                value={formData.pattern}
                onChange={(v) => handleChange('pattern', v)}
                options={PATTERNS.map((p) => ({ value: p.value, label: t(p.labelKey) }))}
              />
            </FormRow>

            <FormRow label={t('document.numberingField.counterDigits')} htmlFor="scheme-digits">
              <span className="inline-flex items-center gap-2">
                <input
                  id="scheme-digits"
                  type="number"
                  value={formData.counter_digits === 0 ? '' : String(formData.counter_digits)}
                  onChange={(e) => handleChange('counter_digits', e.target.value ? parseInt(e.target.value, 10) : 0)}
                  placeholder="—"
                  className="w-24 rounded-md border border-input bg-background px-3 py-1.5 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
                />
                {formData.counter_digits !== 0 && (
                  <button
                    type="button"
                    onClick={() => handleChange('counter_digits', 0)}
                    aria-label={t('common.clear')}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    ✕
                  </button>
                )}
              </span>
            </FormRow>

            <FormRow label={t('document.numberingField.documentTypeId')} htmlFor="scheme-type-id">
              <input
                id="scheme-type-id"
                type="text"
                value={formData.document_type_id}
                onChange={(e) => handleChange('document_type_id', e.target.value)}
                placeholder={t('document.numberingField.documentTypeIdPlaceholder')}
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <div className="py-2">
              <ToggleSwitch
                label={t('document.numberingField.active')}
                checked={formData.is_active}
                onChange={(v) => handleChange('is_active', v)}
              />
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Link to="/documents/numbering-schemes">
              <Button type="button" variant="secondary">{t('common.cancel')}</Button>
            </Link>
            <Button type="submit" disabled={isSubmitting || !formData.name.trim() || !formData.prefix.trim()}>
              {isSubmitting ? t('common.creating') : t('common.create')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
});

export default DocumentNumberingSchemeCreatePage;
